import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { format, subDays, isSameDay } from 'date-fns';
import styles from './styles/ContractDetails.module.scss';

// Context.
import { OmaLummeApp } from '../../omaLummeContext';

// Components.
import Accordion from '../Common/Accordion';
import Button from '../Common/Button';
import Loading from '../Common/Loading';
import CustomerContractDetails from './CustomerContractDetails';
import PaperlessService from './PaperlessService';
import { useWindowSize } from '../Layout/Header';

// Actions.
import { fetchContract, getContactMethod } from '../../actions';
// Types.
import { Contract, ContractBasicData, Delivery } from '../../types/contract';
// Utils.
import { formatDate, roundNumber, setPriceFormat } from '../../utils/billing';
import { generateAddress } from '../../utils/address';
import { gaEventIfAllowed } from '../../utils/googleAnalytics';
import { PriceFixTariffNo } from '../../utils/contracts';
import { contractIsEvCharging } from '../../utils/evCharging';

const OomiLockDownDate = new Date("2026-05-25T00:00:00Z");
// const OomiLockDownDate = new Date('2026-08-31T00:00:00Z');
const currentDate = new Date();
const disablePaperlessService = currentDate >= OomiLockDownDate;

type contractProps = {
  contractBasicData: ContractBasicData;
  onlyContract?: boolean;
};

export const deliveryIsVaihtoturva = (delivery: Delivery): boolean => delivery.tariffName.toLowerCase().includes('vaihtoturva');
export const deliveryIsDiscount = (delivery: Delivery): boolean => delivery.tariffName.toLowerCase().includes('alennus');
export const deliveryIsRefund = (delivery: Delivery): boolean => delivery.tariffName.toLowerCase().includes('hyvitys');
export const deliveryIsGreen = (delivery: Delivery): boolean => delivery.tariffName.toLowerCase().includes('uusiutuva');
export const deliveryIsPriceFix = (delivery: Delivery): boolean => delivery.tariffNo === PriceFixTariffNo;
export const deliveryIsEnergyManagement = (delivery: Delivery) => {
  /**
   * 184/176: EnergyManagementFull
   * 185/177: EnergyManagementLite
   * 250/222: EnergyManagementWithBatteries
   */
  const tariffs = import.meta.env.VITE_ENV === 'dev' ? [184, 185, 250] : [176, 177, 250];

  return tariffs.includes(delivery.tariffNo);
};

export const filterDelivery = (delivery: Delivery): boolean => delivery.tariffGroupNo >= 90 && delivery.tariffGroupNo <= 95;

/**
 * The title for and internals of a contract dropdown.
 */
const ContractDetails: React.FunctionComponent<contractProps> = (props: contractProps) => {
  const { t } = useTranslation();

  const { users, offers } = OmaLummeApp.useState();
  const { contractBasicData, onlyContract } = props;
  const { userId, customerType } = users.activeUser;

  const [loadContract, setLoadContract] = React.useState<boolean>(onlyContract ?? false);
  const [contract, setContract] = React.useState<Contract>();
  const [paperlessServiceEligible, setPaperlessServiceEligible] = React.useState<boolean>(false);
  const [paperlessServiceNotEligibleReason, setPaperlessServiceNotEligibleReason] = React.useState<string>('');
  const [currentContactMethod, setCurrentContactMethod] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    async function fetchContractData(): Promise<void> {
      const fetchedContract = await fetchContract(parseInt(contractBasicData.customerNo), userId, contractBasicData.contractNo);
      const contactMethod = await getContactMethod(parseInt(contractBasicData.customerNo), userId, parseInt(contractBasicData.contractNo));

      if (fetchedContract) {
        setContract(fetchedContract);
      }

      if (contactMethod) {
        setCurrentContactMethod(contactMethod.contactMethod);
        setPaperlessServiceEligible(contactMethod.eligibleToChange);
        setPaperlessServiceNotEligibleReason(contactMethod.reason);
      }
    }

    if (loadContract) {
      fetchContractData();
    }
  }, [contractBasicData, loadContract]);

  const dateformat = 'dd.MM.yyyy';
  let contractName =
    contractBasicData.meteringpointAddress !== null ? generateAddress(contractBasicData.meteringpointAddress, false) : t('common:general.contract');

  const windowWidth = useWindowSize()[0];

  const matchingOffer = offers && offers.length > 0 ? offers.find((offer) => offer.contractNo === parseInt(contractBasicData.contractNo)) : undefined;

  let details = <Loading />;

  if (contract) {
    if (!contract.meteringPoint && contract.contractGroup) {
      contractName = contract.contractGroup.contractGroupName;
    }

    let contractDeliveryName = contract.contractGroup ? contract.contractGroup.contractGroupName : '';
    // The following arrays are used because the api has a... curious way of identifying which discounts apply to which products
    // and when to show discounts. Plus sometimes the client doesn't want prices to be shown but do want the element there etc.
    // These are the discount elementNo, the correct one is identified with use of elementsWithDiscountsRET array.
    const possibleDiscountElementsRET: number[] = [21, 22, 23, 24, 26, 27];
    // These are the elements that can have discounts and identify which of the possibleDiscountElementsRET elementNo to use.
    const elementsWithDiscountsRET: number[] = [1, 2, 3, 4, 6, 7];
    // These are elements that we should show but not show the price of.
    const elementsNoPriceRET: number[] = [];
    // Elements that we don't show any part of.
    const hideElementsRET: number[] = [10];

    let discountToShow = 0;

    let activeDeliveries: Delivery[] = [];
    let previousContracts: Delivery[] = [];
    if (contract.activeDeliveries) {
      activeDeliveries = contract.activeDeliveries;
    }
    if (contract.previousContracts) {
      previousContracts = contract.previousContracts;
    }

    const filteredDeliveries = activeDeliveries.filter(
      (d) => !d.elementName.toLowerCase().includes('vaihtoturva') && !d.elementName.toLowerCase().includes('hyvitys'),
    );

    if (contract.deliveries) {
      contract.deliveries.forEach((delivery) => {
        if (elementsWithDiscountsRET.includes(delivery.elementNo) && discountToShow === 0) {
          discountToShow = delivery.elementNo;
        }
      });

      if (filteredDeliveries && filteredDeliveries.length > 0) {
        contractDeliveryName = `${filteredDeliveries[0].externalName}`;
      } else if (contract.nextContract) {
        contractDeliveryName = contract.nextContract.externalName;
      } else if (previousContracts && previousContracts.length > 0) {
        const contractDelivery = previousContracts.find(
          (d) => !d.externalName.toLowerCase().includes('hyvitys') && !d.externalName.toLowerCase().includes('alennus'),
        );
        if (contractDelivery) {
          contractDeliveryName = contractDelivery.externalName;
        }
      }
    }

    const contractTable: React.JSX.Element[] = [];
    let previousContractsTables: React.JSX.Element[] = [];
    const extraServicesTable: React.JSX.Element[] = [];
    let nextContractTable = <></>;

    const contractDetails: React.JSX.Element[] = [];
    const extraServices: React.JSX.Element[] = [];
    const nextContractDetails: React.JSX.Element[] = [];

    // By default all of the descriptions are the same so we just set it as the first one.
    let contractDescription;
    if (filteredDeliveries[0] && filteredDeliveries[0].externalDescription) {
      contractDescription = filteredDeliveries[0].externalDescription;
    }

    const SpotPriceTariffNos = [7, 70, 8].concat(import.meta.env.VITE_ENV === 'dev' ? [172, 116] : [162, 151]);

    let dateTo: Date | undefined = contract.endDate ? new Date(contract.endDate) : undefined;
    let startDate = format(new Date(contract.startDate), dateformat);
    let dateToLocked = false;
    activeDeliveries.forEach((delivery, key) => {
      let formattedPrice;
      // If the current elementNo isn't included in the possible discounts we show it.
      let shouldShowElement = true;

      // By default we hide all the possible discount elements.
      shouldShowElement = !possibleDiscountElementsRET.includes(delivery.elementNo);

      // If it is in the discount list we will check if it is the correct one.
      if (!shouldShowElement && new Date() >= new Date(delivery.tariffFromDate) && new Date() <= new Date(delivery.tariffToDate)) {
        // For all the discount elements it is exactly 20 elements greater than the discountable elements, e.g. 1 has the discount element 21.
        if (elementsWithDiscountsRET.includes(discountToShow) && delivery.elementNo === discountToShow + 20) {
          shouldShowElement = true;
        }
      }

      if (hideElementsRET.includes(delivery.elementNo)) {
        shouldShowElement = false;
      }

      if (shouldShowElement) {
        if (delivery.elementNo !== 10) {
          if (delivery.totalPriceInclVat || delivery.totalPriceInclVat === 0 || delivery.totalPrice || delivery.totalPrice === 0) {
            // There seems to be a spelling mistake in the endpoint "acticeToday".
            const deliveryPrice = customerType === 'business' ? delivery.totalPrice : delivery.totalPriceInclVat;
            if (!elementsNoPriceRET.includes(delivery.elementNo)) {
              // Rounds up the number if it is neccessarry
              formattedPrice = setPriceFormat(roundNumber(deliveryPrice));
              // For some DH there is no regular contractDeliveryName set. I'm not sure what to set it to so we went with the first delivery name.
              if (contractDeliveryName === undefined) {
                contractDeliveryName = delivery.tariffName;
              }
            }
          }
          if (delivery.elementNo === 2 && SpotPriceTariffNos.includes(delivery.tariffNo)) {
            contractDetails.push(
              <div className="table-row" key={key}>
                <span>{delivery.elementName}</span>
                <span>{t('contracts:contracts.spotPrice')}</span>
              </div>,
            );
          } else if (formattedPrice && delivery.contrctGroup === 1 && deliveryIsVaihtoturva(delivery)) {
            extraServices.push(
              <div className="table-row" key={key}>
                <span>{delivery.elementName}</span>
                <span>{`${formattedPrice} ${delivery.priceUnit}`}</span>
                {delivery.externalDescription && <p>{delivery.externalDescription}</p>}
              </div>,
            );
          } else if (formattedPrice && delivery.contrctGroup === 1 && deliveryIsGreen(delivery)) {
            extraServices.push(
              <div className="table-row" key={key}>
                <span>{delivery.externalName}</span>
                <span>{`${formattedPrice} ${delivery.priceUnit}`}</span>
                {delivery.externalDescription && <p>{delivery.externalDescription}</p>}
              </div>,
            );
          } else if (formattedPrice && delivery.contrctGroup === 1 && delivery.elementName.toLowerCase().includes('ilmastoteko')) {
            extraServices.push(
              <div className="table-row" key={key}>
                <span>{delivery.externalName}</span>
                <span>{`${formattedPrice} ${delivery.priceUnit}`}</span>
                {delivery.externalDescription && <p>{delivery.externalDescription}</p>}
              </div>,
            );
          } else if (delivery.elementNo === 11) {
            contractDetails.push(
              <div className="table-row" key={key}>
                <span>{delivery.elementName}</span>
                <span>{t('contracts:contracts.accordingToContract')}</span>
              </div>,
            );
          } else if (delivery.externalName === 'Latauspalvelu' && delivery.elementName !== 'hidden') {
            contractDetails.push(
              <div className="table-row" key={key}>
                <span>{delivery.elementName}</span>
                <span>{`${
                  delivery.amount !== undefined && delivery.amount > 0
                    ? `${delivery.amount} ${t(`manageContracts:manageContracts.TableSpotPriceFixingsUnit`)} * `
                    : ``
                }${formattedPrice} ${delivery.priceUnit}`}</span>
              </div>,
            );
          } else if (
            delivery.elementName === 'hidden' ||
            (delivery.externalName &&
              delivery.externalName.toLowerCase().includes('synergiasähkö') &&
              delivery.elementName.toLowerCase() === 'yleisenergia')
          ) {
            contractDetails.push(<Fragment key={`empty-${Math.random()}`}></Fragment>);
          } else if (
            formattedPrice &&
            delivery.elementName.toLowerCase() !== 'liittymismaksu' &&
            delivery.elementName.toLowerCase() !== 'kokonaiskulutus' &&
            delivery.elementName.toLowerCase() !== 'np keskihinta'
          ) {
            if (delivery.elementName.toLowerCase().includes('ylittävä kulutus')) {
              contractDetails.push(
                <div className="table-row" key={key}>
                  <span>{delivery.elementName}</span>
                  <span>{t('contracts:contracts.monthlySpotPlusMargin')}</span>
                </div>,
              );
            } else if (delivery.elementName.toLowerCase().includes('kulutusvaikutus')) {
              contractDetails.push(
                <div className="table-row" key={key}>
                  <span>{delivery.elementName}</span>
                  <span>± {delivery.priceUnit}</span>
                </div>,
              );
            } else if (delivery.tariffNo !== 20 && delivery.tariffNo !== 73) {
              contractDetails.push(
                <div className="table-row" key={key}>
                  <span>{delivery.elementName}</span>
                  <span>{`${formattedPrice} ${delivery.priceUnit}`}</span>
                </div>,
              );
            } else {
              // add empty element to array for tariffNo 20 or 73 to show contract title and startDate/endDate
              contractDetails.push(<Fragment key={`empty-${Math.random()}`}></Fragment>);
            }
          } else if (elementsNoPriceRET.includes(delivery.elementNo)) {
            contractDetails.push(
              <div className="table-row" key={key}>
                <span>{delivery.elementName}</span>
              </div>,
            );
          }
          if (possibleDiscountElementsRET.includes(delivery.elementNo)) {
            const date =
              new Date(delivery.specialToDate || new Date()) < new Date(delivery.tariffToDate) ? delivery.specialToDate : delivery.tariffToDate;
            contractDetails.push(
              <div className="table-row" key={`${key}-dateTo`}>
                <span>{t('contracts:contracts.discountTo')}</span>
                <span>{`${format(subDays(new Date(date || new Date()), 1), dateformat)} ${t('contracts:contracts.until')}`}</span>
              </div>,
            );
          }
          if (delivery.tariffNo === (import.meta.env.VITE_ENV === 'dev' ? 191 : 181)) {
            const date =
              new Date(delivery.specialToDate || new Date()) < new Date(delivery.tariffToDate) ? delivery.specialToDate : delivery.tariffToDate;
            contractDetails.push(
              <div className="table-row" key={`${key}-dateTo`}>
                <span>{t('contracts:contracts.warrantyTo')}</span>
                <span>{`${format(subDays(new Date(date || new Date()), 1), dateformat)} ${t('contracts:contracts.until')}`}</span>
              </div>,
            );
          }
          if (delivery.tariffNo === 52 && delivery.elementNo === 13) {
            contractDetails.push(
              <div className="table-row" key={`${key}-invoice-comment`}>
                <span>{contract.invoiceComment}</span>
              </div>,
            );
          }
        }
      }

      if (delivery.tariffFromDate && !filterDelivery(delivery)) {
        startDate = format(new Date(delivery.tariffFromDate), dateformat);
      }
      if (deliveryIsEnergyManagement(delivery)) {
        startDate = format(new Date(delivery.tariffFromDate), dateformat);
        dateTo = subDays(new Date(delivery.tariffToDate), 1);
        dateToLocked = true;
      } else if (delivery.elementNo === 10 && deliveryIsPriceFix(delivery)) {
        dateTo = subDays(new Date(delivery.toDate), 1);
        dateToLocked = true;
      } else if (!filterDelivery(delivery) && !dateToLocked) {
        dateTo = subDays(new Date(delivery.tariffToDate), 1);
      }
    });

    if (contract.nextContract && contract.deliveries) {
      const nextContractElements: string[] = [];

      const deliveryIsFuturePriceFix = (d: Delivery) =>
        d.tariffNo === PriceFixTariffNo &&
        (d.elementNo === 16 || d.elementNo === (import.meta.env.VITE_ENV === 'dev' ? 18 : 17)) &&
        new Date(d.specialFromDate || new Date()) > new Date() &&
        new Date(d.specialToDate || new Date()) > new Date() &&
        !isSameDay(new Date(d.specialToDate || new Date()), new Date('2073-06-01'))
          ? true
          : false;

      // Ending date for pricefix contracts

      const futurePriceFixes = contract.deliveries
        .sort((a, b) => (new Date(a.specialToDate || new Date()) < new Date(b.specialToDate || new Date()) ? -1 : 1))
        .filter((d) => deliveryIsFuturePriceFix(d));

      if (futurePriceFixes.length > 0) {
        const endDate = formatDate(subDays(new Date(futurePriceFixes.at(-1)?.specialToDate || new Date()), 1).toISOString());
        nextContractDetails.push(
          <div className="table-row">
            <span> {t('contracts:contracts.endDate')} </span>
            <span>{endDate}</span>
          </div>,
        );
      }

      contract.deliveries.forEach((delivery, key) => {
        if (
          (new Date(delivery.tariffFromDate) >= new Date(contract.nextContract?.tariffFromDate || new Date()) ||
            deliveryIsFuturePriceFix(delivery)) &&
          !delivery.elementName.toLowerCase().includes('määräaikainen') &&
          delivery.tariffName.toUpperCase().slice(0, 2) !== 'FB' &&
          !nextContractElements.includes(delivery.elementName)
        ) {
          const deliveryPrice = customerType === 'business' ? delivery.totalPrice : delivery.totalPriceInclVat;
          if (delivery.elementNo === 2 && SpotPriceTariffNos.includes(delivery.tariffNo)) {
            nextContractDetails.push(
              <div className="table-row" key={key}>
                <span>{delivery.elementName}</span>
                <span>{t('contracts:contracts.spotPrice')}</span>
              </div>,
            );
            nextContractElements.push(delivery.elementName);
          } else if (delivery.elementName.toLowerCase().includes('kulutusvaikutus')) {
            nextContractDetails.push(
              <div className="table-row" key={key}>
                <span>{delivery.elementName}</span>
                <span>± {delivery.priceUnit}</span>
              </div>,
            );
            nextContractElements.push(delivery.elementName);
          } else if (!delivery.externalName.toLowerCase().includes('salkku') && !nextContractElements.includes(delivery.elementName)) {
            nextContractDetails.push(
              <div className="table-row" key={`${key}-dateTo`}>
                <span>{delivery.elementName}</span>
                <span>
                  {setPriceFormat(roundNumber(deliveryPrice))} {delivery.priceUnit}
                </span>
              </div>,
            );
            nextContractElements.push(delivery.elementName);
          }
        }
      });
    }

    let endDate = dateTo ? format(dateTo, dateformat) : undefined;

    if ((dateTo && (isSameDay(dateTo, new Date('2073-05-31')) || dateTo > new Date('2073-06-01'))) || !endDate) {
      endDate = `${t('contracts:contracts.ongoing')}`;
    }

    const energyManagementDeviceWithoutContract = (contract: Contract) => {
      const energyManagementTariffsWithoutContract = import.meta.env.VITE_ENV === 'dev' ? [182, 186, 187, 191] : [180, 178, 179, 181];
      return contract.deliveries &&
        contract.deliveries.filter((d) => energyManagementTariffsWithoutContract.includes(d.tariffNo)).length > 0 &&
        contract.deliveries.filter((d) => deliveryIsEnergyManagement(d)).length === 0
        ? true
        : false;
    };

    if (energyManagementDeviceWithoutContract(contract)) {
      startDate = t(`contracts:contracts.serviceInActive`);
      endDate = t(`contracts:contracts.serviceInActive`);
    }

    if (contractDetails.length > 0 || (contractDetails.length === 0 && contractIsEvCharging(contractBasicData))) {
      // Just a double check, if the contract is closed then we don't show any active contracts in a closed section.
      if (contract.contractStatus && contract.contractStatus.contractStatusNo.toLowerCase() !== 's') {
        contractTable.push(
          <div className="accordion-table table-responsive" key={``}>
            <div className="table-header">
              <span> {t('contracts:contracts.currentContractGroup')} </span>
              <span>{}</span>
            </div>
            {contractTable.length < 1 && (
              <div className="table-row">
                <span> {t('contracts:contracts.startDate')} </span>
                <span>{startDate}</span>
              </div>
            )}
            {contractTable.length < 1 && (
              <div className="table-row">
                <span> {t('contracts:contracts.endDate')} </span>
                <span>{endDate}</span>
              </div>
            )}
            {contractDetails}
          </div>,
        );
      }
    }

    if (extraServices.length > 0) {
      extraServicesTable.push(
        <div className="accordion-table table-responsive" key={``}>
          <div className="table-header">
            <span> {t('manageContracts:manageContracts.ExtraServicesTitle')} </span>
            <span>{}</span>
          </div>
          {extraServices}
        </div>,
      );
    }
    if (contract.nextContract) {
      nextContractTable = (
        <div className="accordion-table table-responsive">
          <div className="table-header">
            <span> {t('contracts:contracts.nextContract')} </span>
            <span>{contract.nextContract.externalName}</span>
          </div>
          <div className="table-row">
            <span> {t('contracts:contracts.startDate')} </span>
            <span>{format(new Date(contract.nextContract.tariffFromDate), dateformat)}</span>
          </div>
          {nextContractDetails}
        </div>
      );
    }

    let REFUND2023 = 135;

    if (import.meta.env.VITE_ENV === 'dev') {
      REFUND2023 = 134;
    }

    if (contract.previousContracts && contract.previousContracts.length > 0) {
      // Since we have all tariffs for each contract sometimes it would cause many of the same contract to be shown.
      // Thus we just filter out to get unique enddates of the previous contracts.
      previousContracts = contract.previousContracts.filter(
        (v, i, a) => a.findIndex((t) => t.tariffToDate === v.tariffToDate) === i || v.externalName === 'Alennus' || v.tariffNo === REFUND2023,
      );
      previousContractsTables = previousContracts.map((previousContract, key) => {
        let startDate = format(new Date(previousContract.tariffFromDate), dateformat);
        if (contract.endpoint !== 'ret' && (previousContract.contrctGroup === 7 || previousContract.contrctGroup === 15)) {
          startDate = format(new Date(previousContract.signatureDate), dateformat);
        }
        const dateTo = new Date(previousContract.tariffToDate);
        let endDate = format(dateTo, dateformat);
        if (isSameDay(dateTo, new Date('2073-05-31')) || dateTo > new Date('2073-06-01')) {
          endDate = t('contracts:contracts.ongoing');
        }
        const filteredPrevDeliveries = filterDelivery(previousContract) && !deliveryIsRefund(previousContract);
        const filterPrice = setPriceFormat(
          roundNumber(customerType === 'business' ? previousContract.totalPrice : previousContract.totalPriceInclVat),
        );

        const deliveryIsDiscountOrRefund = deliveryIsDiscount(previousContract) || deliveryIsRefund(previousContract);

        const previousContractTable = (
          <div className="table-sub-wrapper" key={key}>
            {!deliveryIsDiscountOrRefund && (
              <>
                <div className="table-header">
                  <span> {t('contracts:contracts.previousContract')} </span>
                  <span>{previousContract.externalName}</span>
                </div>
                <div className="table-row">
                  <span> {t('contracts:contracts.startDate')} </span>
                  <span>{startDate}</span>
                </div>
                <div className="table-row">
                  <span> {t('contracts:contracts.endDate')} </span>
                  <span>{endDate}</span>
                </div>
              </>
            )}
            {filteredPrevDeliveries && (
              <div className="table-row">
                <span>
                  {previousContract.elementName}{' '}
                  {deliveryIsDiscount(previousContract) && (
                    <>
                      ({t(`common:greetings.valid`).toLowerCase()} {startDate}–{endDate})
                    </>
                  )}
                </span>
                <span>{filterPrice}</span>
              </div>
            )}
          </div>
        );
        return previousContractTable;
      });
    }

    const isMobile = windowWidth < 768;

    details = (
      <div className="accordion-content">
        <div className="accordion-inner">
          {matchingOffer && (
            <div className={`${styles.offerDetails} ${matchingOffer.addOnOffer === true ? styles.addOnOffer : ''}`}>
              <div>
                <h2>
                  {matchingOffer.offerTitleOnline} {matchingOffer.renewalOffer === true ? endDate : ''}
                </h2>
                {matchingOffer.offerDescriptionOnline && <p>{matchingOffer.offerDescriptionOnline}</p>}
                <Button
                  className={matchingOffer.addOnOffer === true ? 'buttonTertiaryLime' : 'buttonTertiaryLavender'}
                  link={matchingOffer.offerUrl}
                  newTab={!isMobile}
                  key={matchingOffer.campaignNo}
                  onClick={(): void => {
                    gaEventIfAllowed({
                      category: 'Sopimuksen uusimistarjous',
                      action: 'Tutustu tarjoukseen',
                    });
                  }}
                >
                  {matchingOffer.offerCtaOnline ?? ''}
                </Button>
              </div>
            </div>
          )}
          <div className="details">
            <div className="details-content">
              <h4 className="h2">{contractDeliveryName}</h4>
              {contractDescription && <p>{contractDescription}</p>}
            </div>
          </div>
          {contractTable}
          {extraServicesTable}
          {nextContractTable}
          {previousContractsTables.length > 0 && <div className="accordion-table table-responsive">{previousContractsTables}</div>}
          <CustomerContractDetails contract={contract} />
          {!disablePaperlessService && (paperlessServiceEligible || (!paperlessServiceEligible && paperlessServiceNotEligibleReason === 'changedToday')) &&
            currentContactMethod && (
              <PaperlessService
                contract={contract}
                currentContactMethod={currentContactMethod}
                disabled={!paperlessServiceEligible && paperlessServiceNotEligibleReason === 'changedToday'}
                setDisabled={{ setEligible: setPaperlessServiceEligible, setReason: setPaperlessServiceNotEligibleReason }}
              />
            )}
          {contract.cancellable && (
            <Button
              link="https://www.lumme-energia.fi/sopimuksen-peruutus"
              newTab
            >
              {t('contracts:contracts.cancelContract')}
            </Button>
          )}
          {customerType !== 'business' && <span className="incTax">{t('consumption:consumption.incTax')}</span>}
          {customerType === 'business' && <span className="incTax">{t('consumption:consumption.noIncTax')}</span>}
        </div>
      </div>
    );
  }

  return (
    <Accordion title={contractName} open={onlyContract} offer={matchingOffer} onClick={(): void => setLoadContract(true)}>
      {details}
    </Accordion>
  );
};

export default ContractDetails;
